import type { GlobalSearchResult, ModuleSearchAdapter } from "../types";
import { getSearchClient } from "../apiClient";

interface DriveSearchParent {
  id: string;
  title: string;
  type: "folder" | "file";
}

interface DriveSearchItem {
  id: string;
  title: string;
  updated_at: string;
  type: "folder" | "file";
  parents?: DriveSearchParent[];
}

interface DriveSearchResponse {
  results: DriveSearchItem[];
}

function hrefForItem(item: DriveSearchItem): string {
  if (item.type === "folder") {
    return `/vault/folder/${item.id}`;
  }
  // Files have no detail route in VaultModule; open the parent folder with
  // `?fileId=<id>` so MyVaultRoute can highlight the file in its listing.
  const parentId = item.parents?.[item.parents.length - 1]?.id;
  return parentId
    ? `/vault/folder/${parentId}?fileId=${item.id}`
    : `/vault?fileId=${item.id}`;
}

function subtitleForItem(item: DriveSearchItem): string | undefined {
  const titles = (item.parents ?? [])
    .map((p) => p.title)
    .filter((t): t is string => Boolean(t));
  return titles.length > 0 ? titles.join(" / ") : undefined;
}

function mapItem(i: DriveSearchItem): GlobalSearchResult {
  return {
    id: `drive:${i.id}`,
    moduleId: "drive",
    title: i.title,
    subtitle: subtitleForItem(i),
    timestamp: i.updated_at,
    href: hrefForItem(i),
  };
}

export const driveAdapter: ModuleSearchAdapter = {
  moduleId: "drive",
  label: "Vault",
  async search(q, signal) {
    // Drive's SearchItemFilter filters by `title` (icontains). Sending `q=`
    // is silently ignored and returns the user's first items unfiltered.
    const res = await getSearchClient().get<DriveSearchResponse>(
      `/api/drive/items/search/?title=${encodeURIComponent(q)}&page_size=3`,
      { signal },
    );
    return res.results.map(mapItem);
  },
};
